/* Hours sheet export — one worker, one pay period (Reports → Payroll hours).
   Pure: no DOM, no store, no clock unless one is passed in. The card
   (hoursexport.tsx) renders it; scripts/unit-hours.mjs tests it.

   WHERE THE HOURS COME FROM. Every time entry lives on its job:
   wo.timeLogs = [{ id, hours, date, crewId, clockInAt, clockOutAt, breakMins,
   note, gps: { distMi, ok, noSite, override: { reason, at, withCode } },
   gpsOut: { distMi, ok, noSite, error }, adjusted: { from, at, reason, by } }].
   An entry is CREW hours: the crew clocked in and out together, and the app
   prices labor the same way (crew rate = every member's wage). So each entry
   counts once for every member on that crew; a crew with no members listed is
   one worker under the crew's name.

   PAY PERIODS (settings.payroll):
     weekly       7 days from the chosen weekday
     biweekly     14 days, counted from a date that started a pay period
     semimonthly  the 1st–15th and the 16th–end of the month
   OVERTIME is weekly, past 40 hours, over the WORKWEEK (7 days from the
   weekday a week starts on — for biweekly, the anchor's weekday). A workweek
   that crosses a pay period boundary still counts all its hours: the hours
   worked before the period count toward the 40, and only the overtime that
   falls inside the period is reported in it. Hours go in the order they were
   worked, so the entry that crosses 40 is the one flagged. */

export type PayPeriodKind = "weekly" | "biweekly" | "semimonthly";
export type Payroll = { period: PayPeriodKind; weekStart: number; anchor: string | null };
export type Period = { start: string; end: string; label: string };

export const OT_WEEKLY = 40;
export const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/* ---- dates as YYYY-MM-DD, in the device's local calendar ---- */
const pad = (n: number) => String(n).padStart(2, "0");
export const ymdOf = (d: Date) => d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
const parse = (ymd: string) => { const [y, m, d] = ymd.split("-").map(Number); return new Date(y, m - 1, d, 12); };   // noon: DST-proof day math
export const addDays = (ymd: string, n: number) => { const d = parse(ymd); d.setDate(d.getDate() + n); return ymdOf(d); };
const dow = (ymd: string) => parse(ymd).getDay();
const daysBetween = (a: string, b: string) => Math.round((parse(b).getTime() - parse(a).getTime()) / 86400000);
const isYmd = (s: any) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
const monthDay = (ymd: string) => parse(ymd).toLocaleDateString("en-US", { month: "short", day: "numeric" });

/* settings.payroll, with defaults: weekly, Monday */
export function payrollOf(settings: any): Payroll {
  const p = (settings && settings.payroll) || {};
  const period: PayPeriodKind = p.period === "biweekly" || p.period === "semimonthly" ? p.period : "weekly";
  const anchor = isYmd(p.anchor) ? p.anchor : null;
  let weekStart = Number.isInteger(p.weekStart) && p.weekStart >= 0 && p.weekStart <= 6 ? p.weekStart : 1;
  if (period === "biweekly" && anchor) weekStart = dow(anchor);   // the anchor's weekday IS the week start
  return { period, weekStart, anchor };
}

/* the first day of the workweek holding `ymd` */
export function workweekStart(ymd: string, weekStart: number): string {
  return addDays(ymd, -((dow(ymd) - weekStart + 7) % 7));
}

/* the pay period holding `ymd` */
export function periodFor(ymd: string, p: Payroll): Period {
  let start: string, end: string;
  if (p.period === "semimonthly") {
    const d = parse(ymd);
    const first = d.getDate() <= 15;
    start = ymd.slice(0, 8) + (first ? "01" : "16");
    end = first ? ymd.slice(0, 8) + "15" : ymdOf(new Date(d.getFullYear(), d.getMonth() + 1, 0, 12));
  } else if (p.period === "biweekly") {
    // counted from the anchor (or, with none, from the week start on/after 2026-01-01)
    const anchor = p.anchor || workweekStart("2026-01-07", p.weekStart);
    const off = ((daysBetween(anchor, ymd) % 14) + 14) % 14;
    start = addDays(ymd, -off);
    end = addDays(start, 13);
  } else {
    start = workweekStart(ymd, p.weekStart);
    end = addDays(start, 6);
  }
  const y = (s: string) => s.slice(0, 4);
  return { start, end, label: monthDay(start) + " – " + monthDay(end) + (y(start) !== y(end) ? " " + y(start) + "/" + y(end) : ", " + y(end)) };
}

/* the period holding `today` and the ones before it, newest first */
export function recentPeriods(today: string, p: Payroll, n = 8): Period[] {
  const out: Period[] = [];
  let cur = periodFor(today, p);
  for (let i = 0; i < n; i++) { out.push(cur); cur = periodFor(addDays(cur.start, -1), p); }
  return out;
}

/* ---- the rows ---- */
export type HoursRow = {
  worker: string; crew: string; date: string; jobId: string; job: string; address: string;
  entryId: string; clockIn: number | null; clockOut: number | null; hours: number; breakMins: number | null;
  gpsIn: GpsCell | null; gpsOut: GpsCell | null;
  override: { kind: string; by: string; at: number | null; reason: string; from: number | null } | null;
  note: string;
  weekStart: string; weekToDate: number; otHours: number; inPeriod: boolean;
};
export type GpsCell = { distMi: number | null; where: "inside" | "outside" | "no site pin" | "no fix" };

const round2 = (n: number) => Math.round(n * 100) / 100;

function gpsCell(g: any): GpsCell | null {
  if (!g || typeof g !== "object") return null;
  const dist = g.distMi != null && isFinite(Number(g.distMi)) ? Number(g.distMi) : null;
  const where = g.noSite ? "no site pin" : g.ok === true ? "inside" : g.ok === false ? (dist == null ? "no fix" : "outside") : (dist == null ? "no fix" : "inside");
  return { distMi: dist, where };
}

function overrideOf(e: any): HoursRow["override"] {
  const o = e && e.gps && e.gps.override;
  const a = e && e.adjusted;
  if (!o && !a) return null;
  const kinds = [o ? "GPS override" : "", a ? "Hours adjusted" : ""].filter(Boolean);
  return {
    kind: kinds.join(" + "),
    // a GPS override is the crew's, with the office's code when one is set; an adjustment is the office's
    by: [o ? (o.withCode ? "crew, office code" : "crew") : "", a ? String(a.by || "office") : ""].filter(Boolean).join(" · "),
    at: (a && Number(a.at)) || (o && Number(o.at)) || null,
    reason: [o ? String(o.reason || "") : "", a ? String(a.reason || "") : ""].filter(Boolean).join(" · "),
    from: a && a.from != null && isFinite(Number(a.from)) ? Number(a.from) : null,
  };
}

/* the day an entry belongs to: its date, else the day it was clocked in */
function entryDate(e: any): string | null {
  if (isYmd(e && e.date)) return e.date;
  const t = Number((e && (e.clockInAt || e.createdAt)) || 0);
  return t > 0 ? ymdOf(new Date(t)) : null;
}

/* who an entry counts for: every member of its crew, or the crew itself */
export function workersOf(crew: any): string[] {
  const names = (Array.isArray(crew && crew.members) ? crew.members : [])
    .map((m: any) => String((m && (typeof m === "string" ? m : m.name)) || "").trim()).filter(Boolean);
  if (names.length) return [...new Set(names)] as string[];
  return [String((crew && (crew.foreman || crew.name)) || "Unassigned crew").trim()];
}

/* Every entry in the period, for every worker it counts for, with the running
   workweek total and the overtime each one carries. `workOrders` and `crews`
   are the store's; `worker` narrows it to one person. */
export function hoursSheet({ workOrders, crews, period, weekStart, worker = null }: { workOrders: any[]; crews: any[]; period: Period; weekStart: number; worker?: string | null }) {
  const crewById = new Map((crews || []).map((c: any) => [c && c.id, c]));
  // widen to whole workweeks, so hours before the period count toward the 40
  const from = workweekStart(period.start, weekStart);
  const to = addDays(workweekStart(period.end, weekStart), 6);
  const rows: HoursRow[] = [];
  for (const wo of workOrders || []) {
    for (const e of Array.isArray(wo && wo.timeLogs) ? wo.timeLogs : []) {
      const date = entryDate(e);
      const hours = Number(e && e.hours) || 0;
      if (!date || date < from || date > to || !(hours > 0)) continue;
      const crewId = (e && e.crewId) || wo.crewId || null;
      const crew = crewId ? crewById.get(crewId) : null;
      for (const w of workersOf(crew)) {
        if (worker && w !== worker) continue;
        rows.push({
          worker: w, crew: crew ? String(crew.name || "") : "", date,
          jobId: String(wo.id || ""), job: String(wo.customer || wo.title || "Job"), address: String(wo.address || ""),
          entryId: String((e && e.id) || ""),
          clockIn: Number(e.clockInAt) || null, clockOut: Number(e.clockOutAt) || null,
          hours: round2(hours), breakMins: e.breakMins != null && isFinite(Number(e.breakMins)) ? Math.round(Number(e.breakMins)) : null,
          gpsIn: gpsCell(e.gps), gpsOut: gpsCell(e.gpsOut),
          override: overrideOf(e), note: String((e && e.note) || ""),
          weekStart: workweekStart(date, weekStart), weekToDate: 0, otHours: 0,
          inPeriod: date >= period.start && date <= period.end,
        });
      }
    }
  }
  // worked order: day, then clock-in time (a manual entry sorts after the clocked ones that day)
  rows.sort((a, b) => a.worker.localeCompare(b.worker) || a.date.localeCompare(b.date)
    || (a.clockIn || Infinity) - (b.clockIn || Infinity) || a.jobId.localeCompare(b.jobId) || a.entryId.localeCompare(b.entryId));
  const run = new Map<string, number>();
  for (const r of rows) {
    const k = r.worker + "|" + r.weekStart;
    const before = run.get(k) || 0;
    const after = round2(before + r.hours);
    run.set(k, after);
    r.weekToDate = after;
    r.otHours = round2(Math.max(0, after - Math.max(OT_WEEKLY, before)));
  }

  const inPeriod = rows.filter((r) => r.inPeriod);
  const workers = [...new Set(inPeriod.map((r) => r.worker))].map((name) => {
    const mine = inPeriod.filter((r) => r.worker === name);
    const days = [...new Set(mine.map((r) => r.date))].map((date) => {
      const list = mine.filter((r) => r.date === date);
      return { date, rows: list, total: round2(list.reduce((s, r) => s + r.hours, 0)), otHours: round2(list.reduce((s, r) => s + r.otHours, 0)) };
    });
    const weeks = [...new Set(mine.map((r) => r.weekStart))].map((ws) => {
      const all = rows.filter((r) => r.worker === name && r.weekStart === ws);
      const inside = all.filter((r) => r.inPeriod);
      const total = round2(all.reduce((s, r) => s + r.hours, 0));
      const hours = round2(inside.reduce((s, r) => s + r.hours, 0));
      const ot = round2(inside.reduce((s, r) => s + r.otHours, 0));
      // `before`: hours earlier in this workweek that fell in the previous pay
      // period — they count toward the 40, and were paid there
      const before = round2(all.filter((r) => r.date < period.start).reduce((s, r) => s + r.hours, 0));
      return { start: ws, end: addDays(ws, 6), total, hours, before, regular: round2(hours - ot), otHours: ot, overtime: ot > 0 };
    });
    const hours = round2(mine.reduce((s, r) => s + r.hours, 0));
    const ot = round2(mine.reduce((s, r) => s + r.otHours, 0));
    return { worker: name, crew: mine[0].crew, days, weeks, hours, regular: round2(hours - ot), otHours: ot };
  });
  return { period, weekStart, workers, rows: inPeriod };
}

/* ---- CSV: one row per entry, then the day, week and period totals ---- */
export const CSV_HEADER = ["Row", "Worker", "Crew", "Date", "Job", "Address", "Clock in", "Clock out", "Hours", "Breaks (min)",
  "GPS in", "GPS in (mi to site)", "GPS out", "GPS out (mi to site)", "Override", "Override by", "Override at", "Override reason", "Original hours",
  "Day total", "Week to date", "Overtime hours", "Overtime (over 40/wk)"];

const clock = (t: number | null) => (t ? new Date(t).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "");
const stamp = (t: number | null) => (t ? ymdOf(new Date(t)) + " " + clock(t) : "");
const num = (n: number | null | undefined) => (n == null ? "" : String(round2(n)));
const gpsText = (g: GpsCell | null) => (g ? g.where : "");

export function hoursCsvRows(sheet: ReturnType<typeof hoursSheet>): string[][] {
  const out: string[][] = [CSV_HEADER];
  const blank = (n: number) => Array.from({ length: n }, () => "");
  for (const w of sheet.workers) {
    for (const d of w.days) {
      d.rows.forEach((r, i) => {
        const o = r.override;
        out.push(["entry", r.worker, r.crew, r.date, r.job, r.address, clock(r.clockIn), clock(r.clockOut), num(r.hours), r.breakMins == null ? "" : String(r.breakMins),
          gpsText(r.gpsIn), num(r.gpsIn && r.gpsIn.distMi), gpsText(r.gpsOut), num(r.gpsOut && r.gpsOut.distMi),
          o ? o.kind : "", o ? o.by : "", o ? stamp(o.at) : "", o ? o.reason : "", o ? num(o.from) : "",
          i === d.rows.length - 1 ? num(d.total) : "", num(r.weekToDate), r.otHours ? num(r.otHours) : "", r.otHours ? "OVERTIME" : ""]);
      });
    }
    for (const wk of w.weeks) {
      out.push(["week total", w.worker, w.crew, wk.start + " to " + wk.end, "", "", "", "", num(wk.hours), "", ...blank(9), "", num(wk.total), wk.otHours ? num(wk.otHours) : "", wk.overtime ? "OVERTIME" : ""]);
    }
    out.push(["period total", w.worker, w.crew, sheet.period.start + " to " + sheet.period.end, "", "", "", "", num(w.hours), "", ...blank(9), "", "", w.otHours ? num(w.otHours) : "", w.otHours ? "OVERTIME" : ""]);
  }
  return out;
}

export const csvCell = (v: any) => { const s = v == null ? "" : String(v); return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
export const toCsv = (rows: string[][]) => rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
